import { useState, useEffect, useRef, Children } from "react";
import { AppShell } from "../components/AppShell";
import { InlineIcon } from "../components/InlineIcon";
import { C } from "../styles/tokens";
import {
  createJobApi,
  fetchHRJobsApi,
  updateJobStatusApi,
  deleteJobApi,
} from "../api/jobs";
import { INDUSTRY_MASTER } from "../constants/industries";
import {
  CLOSED_STATUS,
  JOB_DESCRIPTION_MAX_LENGTH,
  JOB_TITLE_MAX_LENGTH,
  PAUSED_STATUS,
  PUBLISHED_STATUS,
  canToggleJob,
  isDeleteRefused,
  isJobLive,
  jobRemovalAction,
  jobStatusLabel,
  toggledJobStatus,
  withJobStatus,
  withoutJob,
} from "../utils/hiring";
import { fetchHRApplicantSummariesApi } from "../utils/hiringApi";
import { todayInIndia } from "../utils/ledger";
import { formatRupees } from "../utils/money";

export function PostOpportunityPage() {
  const [isActive, setIsActive] = useState(true);
  const [activeModal, setActiveModal] = useState(null); // null | 1 | 2 | 3 | 4
  const [showReviewModal, setShowReviewModal] = useState(false);

  // The organisation's jobs and the applications to them, as the server
  // keeps them (GET /api/jobs/hr and /api/jobs/hr/applicants). The
  // applications tell which jobs may still be deleted.
  const [publishedJobs, setPublishedJobs] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [listsRequest, setListsRequest] = useState(0); // bumped to reload
  const [listsError, setListsError] = useState("");
  // The job being switched on or off, and why the last switch failed.
  const [pendingJobId, setPendingJobId] = useState(null);
  const [jobListError, setJobListError] = useState("");
  // The job being deleted or closed: { job, kind: "delete" | "close" }.
  const [jobAction, setJobAction] = useState(null);
  const [jobActionBusy, setJobActionBusy] = useState(false);
  const [jobActionError, setJobActionError] = useState("");
  // Publishing the job in the review modal. The job keeps one id until it
  // is saved, so a retry after a lost answer saves the same job again
  // rather than posting it twice (the server updates its own job by id).
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const postingJobIdRef = useRef(null);
  // The status the job being written is posted with, as the header reads it.
  const newJobStatus = isActive ? PUBLISHED_STATUS : PAUSED_STATUS;
  const newJobStatusText = `${jobStatusLabel({ status: newJobStatus })} (${isActive ? "ON" : "OFF"})`;

  useEffect(() => {
    let current = true;
    setListsError("");
    Promise.all([fetchHRJobsApi(), fetchHRApplicantSummariesApi()])
      .then(([jobs, applications]) => {
        if (!current) return;
        setPublishedJobs(Array.isArray(jobs) ? jobs : []);
        setApplicants(Array.isArray(applications) ? applications : []);
      })
      .catch((err) => {
        if (current) setListsError(err.message);
      });
    return () => {
      current = false;
    };
  }, [listsRequest]);

  const reloadLists = () => setListsRequest((count) => count + 1);

  // View & Pagination State
  const [viewMode, setViewMode] = useState("card"); // "card" | "list"
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedJobDetails, setSelectedJobDetails] = useState(null);
  const itemsPerPage = 4;

  // Main Header State
  const [title, setTitle] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [jobDescription, setJobDescription] = useState("");

  // Card 1 State (Job Requirements)
  const [card1Data, setCard1Data] = useState({
    openingFor: "",
    ageRange: "",
    customMinAge: "",
    customMaxAge: "",
    gender: "Any",
    languagesRequired: "",
    educationLevel: "",
    requiredDegree: "",
    educationalInstitution: "",
    certificationsRequired: "",
    veteranConsideration: "No",
    industry: "",
    departmentSpecialisation: "",
    essentialSkillset: "",
    minExpYears: "",
    maxExpYears: "",
    employmentType: "",
    openPositions: "",
  });

  // Card 2 State
  const [card2Data, setCard2Data] = useState({
    minSalary: "",
    maxSalary: "",
    joiningBonus: "",
    performanceBonus: "",
    healthInsurance: "",
    statutoryBenefits: "",
    esopsOffered: "",
    learningBudget: "",
    noticePeriod: "",
    minimumStayMonths: "",
  });

  // Card 3 State
  const [card3Data, setCard3Data] = useState({
    workingModel: "",
    jobLocation: "",
    localTravel: "",
    domesticTravel: "",
    internationalTravel: "",
    workShift: "",
    maxHoursPerDay: "",
    flexibleHours: "",
    extendBeyondHours: "",
    weekendWork: "",
    reportingStructure: "",
    probationMonths: "",
    interviewMode: "",
    backgroundCheck: "",
    visaSponsorship: "",
    companyEquipment: "",
  });

  // Card 4 State
  const [card4Data, setCard4Data] = useState({
    cabService: "",
    cafeteria: "",
    leavePolicy: "",
    safetyProvisions: "",
    wellnessSupport: "",
    diversityInclusion: "",
    teamCulture: "",
    dressCode: "",
  });

  // Validation State
  const [validationErrors, setValidationErrors] = useState([]);

  // Check mandatory fields requirement
  const getMissingMandatoryFields = () => {
    const missing = [];

    if (!title.trim()) missing.push("Opportunity Title");
    if (!jobDescription.trim()) missing.push("Job Description");
    if (!fromDate.trim()) missing.push("Application Start Date (From)");
    if (!toDate.trim()) missing.push("Application Closing Date (To)");
    const todayStr = todayInIndia();
    if (fromDate.trim() && fromDate < todayStr) {
      missing.push(
        "Application Start Date (From) cannot be in the past (must be today or later)",
      );
    }
    if (fromDate.trim() && toDate.trim() && toDate < fromDate) {
      missing.push(
        "Application Closing Date (To) must be on or after Application Start Date (From)",
      );
    }

    // Card 1 Mandatory Fields
    if (!card1Data.openingFor.trim())
      missing.push("Opening for (in 1. Job Requirements)");
    if (!card1Data.minExpYears.trim())
      missing.push("Min Experience Required (in 1. Job Requirements)");
    if (!card1Data.maxExpYears.trim())
      missing.push("Max Experience Required (in 1. Job Requirements)");
    if (!card1Data.essentialSkillset.trim())
      missing.push("Essential Skillset (in 1. Job Requirements)");

    // Card 2 Mandatory Fields
    if (!card2Data.minSalary.trim())
      missing.push("Minimum Salary (in 2. Compensation & Benefits)");
    if (!card2Data.maxSalary.trim())
      missing.push("Maximum Salary (in 2. Compensation & Benefits)");
    if (!card2Data.statutoryBenefits.trim())
      missing.push(
        "Provident Fund / ESI / Statutory Benefits (in 2. Compensation & Benefits)",
      );
    if (!card2Data.noticePeriod.trim())
      missing.push("Notice Period (in 2. Compensation & Benefits)");

    // Card 3 Mandatory Fields
    if (!card3Data.jobLocation.trim())
      missing.push("Job Location (in 3. Work Conditions)");

    return missing;
  };

  const isCard1Complete = Boolean(
    card1Data.openingFor.trim() &&
    card1Data.minExpYears.trim() &&
    card1Data.maxExpYears.trim() &&
    card1Data.essentialSkillset.trim(),
  );
  const isCard2Complete = Boolean(
    card2Data.minSalary.trim() &&
    card2Data.maxSalary.trim() &&
    card2Data.statutoryBenefits.trim() &&
    card2Data.noticePeriod.trim(),
  );
  const isCard3Complete = Boolean(card3Data.jobLocation.trim());

  const updateCard1 = (field, val) =>
    setCard1Data((prev) => ({ ...prev, [field]: val }));
  const updateCard2 = (field, val) =>
    setCard2Data((prev) => ({ ...prev, [field]: val }));
  const updateCard3 = (field, val) =>
    setCard3Data((prev) => ({ ...prev, [field]: val }));
  const updateCard4 = (field, val) =>
    setCard4Data((prev) => ({ ...prev, [field]: val }));

  // Handle "Publish Opportunity" Click with Mandatory Validation
  const handlePublishClick = (e) => {
    e.preventDefault();
    const missing = getMissingMandatoryFields();
    if (missing.length > 0) {
      setValidationErrors(missing);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setValidationErrors([]);
    setSaveError("");
    setShowReviewModal(true);
  };

  const resetFormState = () => {
    setTitle("");
    setFromDate("");
    setToDate("");
    setJobDescription("");
    setIsActive(true);

    setCard1Data({
      openingFor: "",
      ageRange: "",
      customMinAge: "",
      customMaxAge: "",
      gender: "Any",
      languagesRequired: "",
      educationLevel: "",
      requiredDegree: "",
      educationalInstitution: "",
      certificationsRequired: "",
      veteranConsideration: "No",
      industry: "",
      departmentSpecialisation: "",
      essentialSkillset: "",
      minExpYears: "",
      maxExpYears: "",
      employmentType: "",
      openPositions: "",
    });

    setCard2Data({
      minSalary: "",
      maxSalary: "",
      joiningBonus: "",
      performanceBonus: "",
      healthInsurance: "",
      statutoryBenefits: "",
      esopsOffered: "",
      learningBudget: "",
      noticePeriod: "",
      minimumStayMonths: "",
    });

    setCard3Data({
      workingModel: "",
      jobLocation: "",
      localTravel: "",
      domesticTravel: "",
      internationalTravel: "",
      workShift: "",
      maxHoursPerDay: "",
      flexibleHours: "",
      extendBeyondHours: "",
      weekendWork: "",
      reportingStructure: "",
      probationMonths: "",
      interviewMode: "",
      backgroundCheck: "",
      visaSponsorship: "",
      companyEquipment: "",
    });

    setCard4Data({
      cabService: "",
      cafeteria: "",
      leavePolicy: "",
      safetyProvisions: "",
      wellnessSupport: "",
      diversityInclusion: "",
      teamCulture: "",
      dressCode: "",
    });

    setValidationErrors([]);
  };

  // Handle "Done" inside Review Modal
  const handleDoneConfirm = async () => {
    if (saving) return;
    const missing = getMissingMandatoryFields();
    if (missing.length > 0) {
      setValidationErrors(missing);
      setShowReviewModal(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (!postingJobIdRef.current) postingJobIdRef.current = `job-${Date.now()}`;
    const payload = {
      id: postingJobIdRef.current,
      title,
      jobDescription,
      fromDate,
      toDate,
      status: newJobStatus,
      card1: { ...card1Data },
      card2: { ...card2Data },
      card3: { ...card3Data },
      card4: { ...card4Data },
    };

    // The job joins the list only as the server saved it; a refusal (an
    // organisation not yet approved, a field too long) is shown here.
    setSaving(true);
    setSaveError("");
    try {
      const created = await createJobApi(payload);
      postingJobIdRef.current = null;
      setPublishedJobs((jobs) => [created, ...withoutJob(jobs, created.id)]);
      resetFormState();
      setShowReviewModal(false);
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Switch a job on (Active) or off (Inactive) on the server; the list
  // changes only when the server has answered.
  const toggleJobStatus = async (job) => {
    if (pendingJobId) return;
    setJobListError("");
    if (!canToggleJob(job, todayInIndia())) {
      setJobListError(
        `"${job.title}" cannot take applications again: its closing date (${job.toDate}) has passed.`,
      );
      return;
    }
    setPendingJobId(job.id);
    try {
      const updated = await updateJobStatusApi(job.id, toggledJobStatus(job));
      setPublishedJobs((jobs) => withJobStatus(jobs, job.id, updated.status));
    } catch (err) {
      setJobListError(err.message);
    } finally {
      setPendingJobId(null);
    }
  };

  // Ask before deleting a job (nobody has applied) or closing it.
  const requestJobAction = (job, kind) => {
    setJobActionError("");
    setJobAction({ job, kind });
  };

  const closeJobAction = () => {
    if (jobActionBusy) return;
    setJobAction(null);
  };

  const confirmJobAction = async () => {
    if (!jobAction || jobActionBusy) return;
    const { job, kind } = jobAction;
    setJobActionBusy(true);
    setJobActionError("");
    try {
      if (kind === "delete") {
        await deleteJobApi(job.id);
        setPublishedJobs((jobs) => withoutJob(jobs, job.id));
      } else {
        const updated = await updateJobStatusApi(job.id, CLOSED_STATUS);
        setPublishedJobs((jobs) => withJobStatus(jobs, job.id, updated.status));
      }
      setJobAction(null);
    } catch (err) {
      // Someone applied since the list was loaded: offer closing instead,
      // and reload the applications so the list offers it too.
      if (kind === "delete" && isDeleteRefused(err)) {
        setJobAction({ job, kind: "close" });
        reloadLists();
      }
      setJobActionError(err.message);
    } finally {
      setJobActionBusy(false);
    }
  };

  // Pagination Logic
  const totalPages = Math.ceil(publishedJobs.length / itemsPerPage);
  const paginatedJobs = publishedJobs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const jobActionTitle = jobAction?.job.title || "this job listing";

  // A deleted job can empty the last page, whose controls then disappear.
  useEffect(() => {
    if (currentPage > 1 && currentPage > totalPages) {
      setCurrentPage(Math.max(totalPages, 1));
    }
  }, [currentPage, totalPages]);

  return (
    <AppShell maxWidth="100%">
      <div
        style={{
          background: "#fff",
          padding: 28,
          borderRadius: 12,
          border: `1px solid ${C.divider}`,
        }}
      >
        {/* Header Bar */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
          }}
        >
          <h1
            style={{ fontSize: 24, fontWeight: 800, color: C.navy, margin: 0 }}
          >
            Post Opportunity
          </h1>

          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              cursor: "pointer",
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            <span>
              Status:{" "}
              <strong style={{ color: isActive ? C.green : C.red }}>
                {newJobStatusText}
              </strong>
            </span>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              style={{ width: 18, height: 18, cursor: "pointer" }}
            />
          </label>
        </div>

        {/* Validation Errors Alert */}
        {validationErrors.length > 0 && (
          <div
            style={{
              background: "#fff1f2",
              border: `1px solid ${C.red}`,
              borderRadius: 8,
              padding: 16,
              marginBottom: 20,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: C.red,
                fontWeight: 800,
                fontSize: 14,
                marginBottom: 6,
              }}
            >
              <InlineIcon name="warning" />
              Mandatory Fields Required. Fill in every mandatory field to post
              this opportunity.
            </div>
            <div style={{ fontSize: 13, color: C.textDark, marginBottom: 8 }}>
              The job cannot be posted until the following required fields are
              filled out:
            </div>
            <ul
              style={{
                margin: 0,
                paddingLeft: 20,
                color: C.red,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {validationErrors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Basic Header Form */}
        <form
          onSubmit={handlePublishClick}
          style={{ display: "flex", flexDirection: "column", gap: 20 }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr",
              gap: 16,
            }}
          >
            <label style={labelStyle}>
              <span>
                Opportunity Title{" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <input
                type="text"
                value={title}
                maxLength={JOB_TITLE_MAX_LENGTH}
                onChange={(e) => setTitle(e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor:
                    !title.trim() && validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                required
              />
            </label>
            <label style={labelStyle}>
              <span>
                Application Start (From){" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <input
                type="date"
                value={fromDate}
                min={todayInIndia()}
                onChange={(e) => {
                  const todayStr = todayInIndia();
                  const newFrom = e.target.value;
                  if (newFrom && newFrom < todayStr) {
                    return;
                  }
                  setFromDate(newFrom);
                  if (toDate && newFrom && toDate < newFrom) {
                    setToDate(newFrom);
                  }
                }}
                style={{
                  ...inputStyle,
                  borderColor:
                    !fromDate.trim() && validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                required
              />
            </label>
            <label style={labelStyle}>
              <span>
                Application Closing (To){" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <input
                type="date"
                value={toDate}
                min={fromDate || todayInIndia()}
                onChange={(e) => {
                  const todayStr = todayInIndia();
                  const minAllowed = fromDate || todayStr;
                  const newTo = e.target.value;
                  if (newTo && newTo < minAllowed) {
                    return;
                  }
                  setToDate(newTo);
                }}
                style={{
                  ...inputStyle,
                  borderColor:
                    !toDate.trim() && validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                required
              />
            </label>
          </div>

          <label style={labelStyle}>
            <span>
              Job Description{" "}
              <span style={{ color: C.red, fontWeight: 800 }}>
                * (Mandatory)
              </span>
            </span>
            <textarea
              placeholder="Provide a detailed job description, roles, key responsibilities, and team expectations..."
              value={jobDescription}
              maxLength={JOB_DESCRIPTION_MAX_LENGTH}
              onChange={(e) => setJobDescription(e.target.value)}
              style={{
                ...textareaStyle,
                minHeight: 140,
                borderColor:
                  !jobDescription.trim() && validationErrors.length > 0
                    ? C.red
                    : C.divider,
              }}
              rows={7}
              required
            />
          </label>

          {/* 4 EQUAL HORIZONTAL CARDS GRID */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 16,
              marginTop: 8,
            }}
          >
            {/* Card 1: Job Requirements */}
            <div
              onClick={() => setActiveModal(1)}
              style={{
                ...interactiveCardStyle,
                borderColor:
                  !isCard1Complete && validationErrors.length > 0
                    ? C.red
                    : C.primary,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: C.primaryLight,
                  padding: "8px 10px",
                  borderBottom: `1px solid ${C.divider}`,
                }}
              >
                <h3
                  style={{
                    ...cardHeaderStyle,
                    background: "transparent",
                    borderBottom: 0,
                    padding: 0,
                  }}
                >
                  1. Job Requirements
                </h3>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "2px 6px",
                    borderRadius: 4,
                    background: isCard1Complete ? "#dcfce7" : "#fee2e2",
                    color: isCard1Complete ? C.green : C.red,
                  }}
                >
                  {isCard1Complete ? (
                    <>
                      <InlineIcon name="check" size={10} /> Filled
                    </>
                  ) : (
                    "* Required"
                  )}
                </span>
              </div>
              <div style={cardBodyStyle}>
                <div style={cardSummaryTextStyle}>
                  <strong>Opening For:</strong>{" "}
                  {card1Data.openingFor || (
                    <span style={{ color: C.red }}>Pending *</span>
                  )}
                  <br />
                  <strong>Degree:</strong> {card1Data.requiredDegree || "-"}
                  <br />
                  <strong>Skillset:</strong>{" "}
                  {card1Data.essentialSkillset ? (
                    card1Data.essentialSkillset.length > 20 ? (
                      card1Data.essentialSkillset.substring(0, 20) + "..."
                    ) : (
                      card1Data.essentialSkillset
                    )
                  ) : (
                    <span style={{ color: C.red }}>Pending *</span>
                  )}
                </div>
                <div style={cardLinkTextStyle}>Configure Questionnaire</div>
              </div>
            </div>

            {/* Card 2: COMPENSATION & BENEFITS */}
            <div
              onClick={() => setActiveModal(2)}
              style={{
                ...interactiveCardStyle,
                borderColor:
                  !isCard2Complete && validationErrors.length > 0
                    ? C.red
                    : C.primary,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: C.primaryLight,
                  padding: "8px 10px",
                  borderBottom: `1px solid ${C.divider}`,
                }}
              >
                <h3
                  style={{
                    ...cardHeaderStyle,
                    background: "transparent",
                    borderBottom: 0,
                    padding: 0,
                  }}
                >
                  2. COMPENSATION & BENEFITS
                </h3>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "2px 6px",
                    borderRadius: 4,
                    background: isCard2Complete ? "#dcfce7" : "#fee2e2",
                    color: isCard2Complete ? C.green : C.red,
                  }}
                >
                  {isCard2Complete ? (
                    <>
                      <InlineIcon name="check" size={10} /> Filled
                    </>
                  ) : (
                    "* Required"
                  )}
                </span>
              </div>
              <div style={cardBodyStyle}>
                <div style={cardSummaryTextStyle}>
                  <strong>Salary:</strong>{" "}
                  {card2Data.minSalary && card2Data.maxSalary ? (
                    `${card2Data.minSalary} - ${card2Data.maxSalary}`
                  ) : (
                    <span style={{ color: C.red }}>Pending *</span>
                  )}
                  <br />
                  <strong>PF / ESI:</strong>{" "}
                  {card2Data.statutoryBenefits || (
                    <span style={{ color: C.red }}>Pending *</span>
                  )}
                  <br />
                  <strong>Notice:</strong>{" "}
                  {card2Data.noticePeriod || (
                    <span style={{ color: C.red }}>Pending *</span>
                  )}
                </div>
                <div style={cardLinkTextStyle}>Configure Pay & Perks</div>
              </div>
            </div>

            {/* Card 3: WORK CONDITIONS */}
            <div
              onClick={() => setActiveModal(3)}
              style={{
                ...interactiveCardStyle,
                borderColor:
                  !isCard3Complete && validationErrors.length > 0
                    ? C.red
                    : C.primary,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: C.primaryLight,
                  padding: "8px 10px",
                  borderBottom: `1px solid ${C.divider}`,
                }}
              >
                <h3
                  style={{
                    ...cardHeaderStyle,
                    background: "transparent",
                    borderBottom: 0,
                    padding: 0,
                  }}
                >
                  3. WORK CONDITIONS
                </h3>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "2px 6px",
                    borderRadius: 4,
                    background: isCard3Complete ? "#dcfce7" : "#fee2e2",
                    color: isCard3Complete ? C.green : C.red,
                  }}
                >
                  {isCard3Complete ? (
                    <>
                      <InlineIcon name="check" size={10} /> Filled
                    </>
                  ) : (
                    "* Required"
                  )}
                </span>
              </div>
              <div style={cardBodyStyle}>
                <div style={cardSummaryTextStyle}>
                  <strong>Location:</strong>{" "}
                  {card3Data.jobLocation || (
                    <span style={{ color: C.red }}>Pending *</span>
                  )}
                  <br />
                  <strong>Model:</strong> {card3Data.workingModel || "-"}
                  <br />
                  <strong>Shift:</strong> {card3Data.workShift || "-"}
                </div>
                <div style={cardLinkTextStyle}>Configure Work Rules</div>
              </div>
            </div>

            {/* Card 4: CULTURE & PERKS */}
            <div onClick={() => setActiveModal(4)} style={interactiveCardStyle}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: C.primaryLight,
                  padding: "8px 10px",
                  borderBottom: `1px solid ${C.divider}`,
                }}
              >
                <h3
                  style={{
                    ...cardHeaderStyle,
                    background: "transparent",
                    borderBottom: 0,
                    padding: 0,
                  }}
                >
                  4. CULTURE & PERKS
                </h3>
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: "2px 6px",
                    borderRadius: 4,
                    background: "#f1f5f9",
                    color: C.textMed,
                  }}
                >
                  Optional
                </span>
              </div>
              <div style={cardBodyStyle}>
                <div style={cardSummaryTextStyle}>
                  <strong>Dress Code:</strong> {card4Data.dressCode || "-"}
                  <br />
                  <strong>Cab Service:</strong> {card4Data.cabService || "-"}
                  <br />
                  <strong>Food:</strong> {card4Data.foodProvided || "-"}
                </div>
                <div style={cardLinkTextStyle}>Configure Perks</div>
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 24,
            }}
          >
            <button type="submit" style={submitButtonStyle}>
              Publish Opportunity
            </button>
          </div>
        </form>
      </div>

      {listsError && (
        <div
          role="alert"
          className="mt-8 flex items-center justify-between gap-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          <span>
            Your published opportunities could not be loaded: {listsError}
          </span>
          <button
            type="button"
            onClick={reloadLists}
            className="shrink-0 cursor-pointer rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-bold text-red-700 hover:bg-red-100"
          >
            Try again
          </button>
        </div>
      )}

      {/* PUBLISHED OPPORTUNITIES SECTION (WITH PAGINATION, LIST/CARD VIEW & ACTIVE TOGGLE) */}
      {publishedJobs.length > 0 && (
        <div
          style={{
            marginTop: 32,
            background: "#fff",
            padding: 28,
            borderRadius: 12,
            border: `1px solid ${C.divider}`,
          }}
        >
          {/* Controls Bar: Title, View Switcher & Count */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <div>
              <h2
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: C.navy,
                  margin: 0,
                }}
              >
                Published Opportunities ({publishedJobs.length})
              </h2>
              <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
                Manage active job listings & statuses
              </div>
            </div>

            {/* Card View vs List View Toggle */}
            <div
              style={{
                display: "flex",
                background: "#f1f5f9",
                padding: 4,
                borderRadius: 8,
                gap: 4,
              }}
            >
              <button
                onClick={() => setViewMode("card")}
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: 0,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  background: viewMode === "card" ? "#fff" : "transparent",
                  color: viewMode === "card" ? C.primary : C.textMed,
                  boxShadow:
                    viewMode === "card" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                }}
              >
                Card View
              </button>
              <button
                onClick={() => setViewMode("list")}
                style={{
                  padding: "6px 14px",
                  borderRadius: 6,
                  border: 0,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: "pointer",
                  background: viewMode === "list" ? "#fff" : "transparent",
                  color: viewMode === "list" ? C.primary : C.textMed,
                  boxShadow:
                    viewMode === "list" ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                }}
              >
                List View
              </button>
            </div>
          </div>

          {jobListError && (
            <p
              role="alert"
              className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
            >
              {jobListError}
            </p>
          )}

          {/* CARD VIEW DISPLAY */}
          {viewMode === "card" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
              }}
            >
              {paginatedJobs.map((job) => (
                <div
                  key={job.id}
                  style={{
                    border: `1px solid ${C.divider}`,
                    padding: 20,
                    borderRadius: 10,
                    background: "#f8fafc",
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                    }}
                  >
                    <div>
                      <h3
                        onClick={() => setSelectedJobDetails(job)}
                        style={{
                          fontSize: 17,
                          fontWeight: 700,
                          color: C.primary,
                          margin: 0,
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        title="Click to view details"
                      >
                        {job.title}
                      </h3>
                      <div
                        style={{ fontSize: 12, color: C.textMed, marginTop: 4 }}
                      >
                        Posted on {job.postedAt}, applications {job.fromDate} to{" "}
                        {job.toDate}
                      </div>
                    </div>

                    {/* Active / Inactive Status Toggle Switch */}
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: isJobLive(job) ? C.green : C.textLight,
                        }}
                      >
                        {jobStatusLabel(job)}
                      </span>
                      <button
                        disabled={pendingJobId === job.id}
                        onClick={() => toggleJobStatus(job)}
                        style={{
                          width: 36,
                          height: 20,
                          borderRadius: 10,
                          border: 0,
                          cursor: "pointer",
                          position: "relative",
                          background: isJobLive(job) ? C.green : "#cbd5e1",
                          transition: "background 0.2s ease",
                        }}
                      >
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            background: "#fff",
                            position: "absolute",
                            top: 2,
                            left: isJobLive(job) ? 18 : 2,
                            transition: "left 0.2s ease",
                          }}
                        />
                      </button>
                    </div>
                  </div>

                  {job.jobDescription && (
                    <div
                      onClick={() => setSelectedJobDetails(job)}
                      style={{
                        fontSize: 12,
                        color: C.textDark,
                        background: "#fff",
                        padding: "8px 12px",
                        borderRadius: 6,
                        border: `1px solid ${C.divider}`,
                        fontStyle: "italic",
                        cursor: "pointer",
                      }}
                    >
                      "
                      {job.jobDescription.length > 120
                        ? job.jobDescription.substring(0, 120) + "..."
                        : job.jobDescription}
                      "
                    </div>
                  )}

                  <div
                    onClick={() => setSelectedJobDetails(job)}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      fontSize: 12,
                      background: "#fff",
                      padding: 12,
                      borderRadius: 6,
                      border: `1px solid ${C.divider}`,
                      cursor: "pointer",
                    }}
                  >
                    <div>
                      <strong>Location:</strong> {job.card3.jobLocation}
                    </div>
                    <div>
                      <strong>Salary:</strong> {job.card2.minSalary} -{" "}
                      {job.card2.maxSalary}
                    </div>
                    <div>
                      <strong>Exp:</strong> {job.card1.minExpYears}-
                      {job.card1.maxExpYears} yrs
                    </div>
                    <div>
                      <strong>Positions:</strong> {job.card1.openPositions}
                    </div>
                  </div>

                  {/* Actions Bar */}
                  <div
                    style={{
                      marginTop: "auto",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingTop: 8,
                    }}
                  >
                    <button
                      onClick={() => setSelectedJobDetails(job)}
                      style={{
                        padding: "6px 14px",
                        borderRadius: 6,
                        border: `1px solid ${C.primary}`,
                        background: C.primaryLight,
                        color: C.primary,
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      View Full Details
                    </button>
                    {jobRemovalAction(job, applicants) === "close" && (
                      <button
                        type="button"
                        onClick={() => requestJobAction(job, "close")}
                        title="People have applied, so the job can be closed but not deleted"
                        className="cursor-pointer rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50"
                      >
                        Close Job
                      </button>
                    )}
                    {jobRemovalAction(job, applicants) === "delete" && (
                      <button
                        disabled={isJobLive(job)}
                        onClick={() => requestJobAction(job, "delete")}
                        title={
                          isJobLive(job)
                            ? "Pause the job (switch it off) to delete it"
                            : "Delete Opportunity"
                        }
                        style={{
                          padding: "6px 12px",
                          borderRadius: 6,
                          border: `1px solid ${isJobLive(job) ? "#e2e8f0" : C.red}`,
                          background: isJobLive(job) ? "#f8fafc" : C.redLight,
                          color: isJobLive(job) ? "#94a3b8" : C.red,
                          fontSize: 12,
                          fontWeight: 700,
                          cursor: isJobLive(job) ? "not-allowed" : "pointer",
                          opacity: isJobLive(job) ? 0.5 : 1,
                        }}
                      >
                        Delete Job
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* LIST VIEW DISPLAY */}
          {viewMode === "list" && (
            <div
              style={{
                border: `1px solid ${C.divider}`,
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 13,
                  textAlign: "left",
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "#f1f5f9",
                      color: C.navy,
                      borderBottom: `1px solid ${C.divider}`,
                    }}
                  >
                    <th style={{ padding: "12px 16px" }}>Job Title</th>
                    <th style={{ padding: "12px 16px" }}>Location</th>
                    <th style={{ padding: "12px 16px" }}>Salary</th>
                    <th style={{ padding: "12px 16px" }}>Status Toggle</th>
                    <th style={{ padding: "12px 16px", textAlign: "right" }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedJobs.map((job) => (
                    <tr
                      key={job.id}
                      style={{
                        borderBottom: `1px solid ${C.divider}`,
                        background: "#fff",
                      }}
                    >
                      <td
                        onClick={() => setSelectedJobDetails(job)}
                        style={{
                          padding: "12px 16px",
                          fontWeight: 700,
                          color: C.primary,
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                      >
                        {job.title}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        {job.card3?.jobLocation || "-"}
                        {job.card3?.workingModel
                          ? ` (${job.card3.workingModel})`
                          : ""}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        {job.card2?.minSalary} - {job.card2?.maxSalary}
                      </td>
                      <td style={{ padding: "12px 16px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <button
                            disabled={pendingJobId === job.id}
                            onClick={() => toggleJobStatus(job)}
                            style={{
                              width: 36,
                              height: 20,
                              borderRadius: 10,
                              border: 0,
                              cursor: "pointer",
                              position: "relative",
                              background: isJobLive(job) ? C.green : "#cbd5e1",
                              transition: "background 0.2s ease",
                            }}
                          >
                            <span
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background: "#fff",
                                position: "absolute",
                                top: 2,
                                left: isJobLive(job) ? 18 : 2,
                                transition: "left 0.2s ease",
                              }}
                            />
                          </button>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: isJobLive(job) ? C.green : C.textLight,
                            }}
                          >
                            {jobStatusLabel(job)}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "right" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 6,
                          }}
                        >
                          <button
                            onClick={() => setSelectedJobDetails(job)}
                            style={{
                              padding: "5px 10px",
                              borderRadius: 4,
                              border: `1px solid ${C.primary}`,
                              background: C.primaryLight,
                              color: C.primary,
                              fontSize: 12,
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            View Details
                          </button>
                          {jobRemovalAction(job, applicants) === "close" && (
                            <button
                              type="button"
                              onClick={() => requestJobAction(job, "close")}
                              title="People have applied, so the job can be closed but not deleted"
                              className="cursor-pointer rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-bold text-slate-700 hover:bg-slate-50"
                            >
                              Close
                            </button>
                          )}
                          {jobRemovalAction(job, applicants) === "delete" && (
                            <button
                              disabled={isJobLive(job)}
                              onClick={() => requestJobAction(job, "delete")}
                              title={
                                isJobLive(job)
                                  ? "Pause the job (switch it off) to delete it"
                                  : "Delete Opportunity"
                              }
                              style={{
                                padding: "5px 10px",
                                borderRadius: 4,
                                border: `1px solid ${isJobLive(job) ? "#e2e8f0" : C.red}`,
                                background: isJobLive(job)
                                  ? "#f8fafc"
                                  : C.redLight,
                                color: isJobLive(job) ? "#94a3b8" : C.red,
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: isJobLive(job)
                                  ? "not-allowed"
                                  : "pointer",
                                opacity: isJobLive(job) ? 0.5 : 1,
                              }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINATION CONTROLS */}
          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 20,
                paddingTop: 16,
                borderTop: `1px solid ${C.divider}`,
              }}
            >
              <div style={{ fontSize: 12, color: C.textMed }}>
                Showing Page <strong>{currentPage}</strong> of{" "}
                <strong>{totalPages}</strong> ({publishedJobs.length} total
                jobs)
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 6,
                    border: `1px solid ${C.divider}`,
                    background: "#fff",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    opacity: currentPage === 1 ? 0.5 : 1,
                    fontWeight: 600,
                  }}
                >
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    style={{
                      padding: "6px 12px",
                      borderRadius: 6,
                      border: `1px solid ${C.divider}`,
                      background: currentPage === i + 1 ? C.primary : "#fff",
                      color: currentPage === i + 1 ? "#fff" : C.textDark,
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((p) => Math.min(p + 1, totalPages))
                  }
                  style={{
                    padding: "6px 14px",
                    borderRadius: 6,
                    border: `1px solid ${C.divider}`,
                    background: "#fff",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    fontWeight: 600,
                  }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* REVIEW & CONFIRM MODAL POPUP */}
      {showReviewModal && (
        <ModalWrapper
          title="Review & Confirm Opportunity Details"
          onClose={() => setShowReviewModal(false)}
        >
          <div
            style={{
              background: C.surface,
              padding: 16,
              borderRadius: 8,
              marginBottom: 16,
              border: `1px solid ${C.divider}`,
            }}
          >
            <div
              style={{
                fontSize: 14,
                color: C.green,
                fontWeight: 700,
                marginBottom: 4,
              }}
            >
              <InlineIcon name="check" /> All mandatory sections completed
            </div>
            <div style={{ fontSize: 12, color: C.textMed }}>
              Review filled & mandatory information below before final
              publication.
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              fontSize: 13,
            }}
          >
            <ReviewSection title="General Opportunity Information">
              <ReviewRow label="Title" value={title} isMandatory />
              <ReviewRow
                label="Job Description"
                value={jobDescription}
                isMandatory
              />
              <ReviewRow
                label="Application Window"
                value={fromDate && toDate ? `${fromDate} to ${toDate}` : ""}
                isMandatory
              />
              <ReviewRow
                label="Post Status"
                value={newJobStatusText}
                isMandatory
              />
            </ReviewSection>

            <ReviewSection title="1. Job Requirements">
              <ReviewRow
                label="Opening For"
                value={card1Data.openingFor}
                isMandatory
              />
              <ReviewRow
                label="Required Degree"
                value={card1Data.requiredDegree}
              />
              <ReviewRow
                label="Experience"
                value={
                  card1Data.minExpYears && card1Data.maxExpYears
                    ? `${card1Data.minExpYears} - ${card1Data.maxExpYears} years`
                    : ""
                }
                isMandatory
              />
              <ReviewRow
                label="Essential Skillset"
                value={card1Data.essentialSkillset}
                isMandatory
              />

              <ReviewRow
                label="Education Level"
                value={card1Data.educationLevel}
              />
              <ReviewRow
                label="Institution"
                value={card1Data.educationalInstitution}
              />
              <ReviewRow label="Age Range" value={card1Data.ageRange} />
              <ReviewRow
                label="Gender"
                value={
                  card1Data.gender && card1Data.gender !== "Any"
                    ? card1Data.gender
                    : ""
                }
              />
              <ReviewRow
                label="Certifications Required"
                value={card1Data.certificationsRequired}
              />
              <ReviewRow
                label="Veteran Consideration"
                value={
                  card1Data.veteranConsideration &&
                  card1Data.veteranConsideration !== "No"
                    ? card1Data.veteranConsideration
                    : ""
                }
              />
              <ReviewRow label="Industry" value={card1Data.industry} />
              <ReviewRow
                label="Department / Specialisation"
                value={card1Data.departmentSpecialisation}
              />
              <ReviewRow
                label="Employment Type"
                value={card1Data.employmentType}
              />
              <ReviewRow
                label="Open Positions"
                value={card1Data.openPositions}
              />
            </ReviewSection>

            <ReviewSection title="2. Compensation & Benefits">
              <ReviewRow
                label="Salary Range"
                value={
                  card2Data.minSalary && card2Data.maxSalary
                    ? `${card2Data.minSalary} - ${card2Data.maxSalary}`
                    : ""
                }
                isMandatory
              />
              <ReviewRow
                label="Statutory Benefits (PF / ESI)"
                value={card2Data.statutoryBenefits}
                isMandatory
              />
              <ReviewRow
                label="Notice Period"
                value={card2Data.noticePeriod}
                isMandatory
              />

              <ReviewRow label="Joining Bonus" value={card2Data.joiningBonus} />
              <ReviewRow
                label="Performance Bonus"
                value={card2Data.performanceBonus}
              />
              <ReviewRow
                label="Health Insurance"
                value={card2Data.healthInsurance}
              />
              <ReviewRow label="ESOPs Offered" value={card2Data.esopsOffered} />
              <ReviewRow
                label="Learning Budget"
                value={card2Data.learningBudget}
              />
              <ReviewRow
                label="Min Stay Commitment"
                value={
                  card2Data.minimumStayMonths
                    ? `${card2Data.minimumStayMonths} months`
                    : ""
                }
              />
            </ReviewSection>

            <ReviewSection title="3. Work Conditions">
              <ReviewRow
                label="Job Location"
                value={card3Data.jobLocation}
                isMandatory
              />

              <ReviewRow label="Working Model" value={card3Data.workingModel} />
              <ReviewRow
                label="Work Shift & Hours"
                value={
                  card3Data.workShift && card3Data.expectedHoursPerDay
                    ? `${card3Data.workShift} Shift (${card3Data.expectedHoursPerDay} hrs/day)`
                    : card3Data.workShift ||
                      (card3Data.expectedHoursPerDay
                        ? `${card3Data.expectedHoursPerDay} hrs/day`
                        : "")
                }
              />
              <ReviewRow
                label="Interview Process"
                value={
                  card3Data.interviewMode && card3Data.interviewRounds
                    ? `${card3Data.interviewMode} (${card3Data.interviewRounds} rounds)`
                    : card3Data.interviewMode ||
                      (card3Data.interviewRounds
                        ? `${card3Data.interviewRounds} rounds`
                        : "")
                }
              />
              <ReviewRow
                label="Background Verification"
                value={card3Data.bgvRequired}
              />
              <ReviewRow
                label="Probation Period"
                value={
                  card3Data.probationMonths
                    ? `${card3Data.probationMonths} months`
                    : ""
                }
              />
              <ReviewRow
                label="Visa Sponsorship"
                value={card3Data.visaSponsorship}
              />
              <ReviewRow
                label="Company Equipment"
                value={card3Data.companyEquipment}
              />
            </ReviewSection>

            <ReviewSection title="4. Culture & Perks">
              <ReviewRow label="Cab Service" value={card4Data.cabService} />
              <ReviewRow label="Food Provided" value={card4Data.foodProvided} />
              <ReviewRow
                label="Leave Policy (CL/SL/EL)"
                value={card4Data.leavePolicyCLELEL}
              />
              <ReviewRow
                label="Safety Provisions"
                value={card4Data.safetyProvisions}
              />
              <ReviewRow
                label="Wellness Support"
                value={card4Data.wellnessSupport}
              />
              <ReviewRow
                label="Diversity & Inclusion"
                value={card4Data.diversityInclusion}
              />
              <ReviewRow label="Team Culture" value={card4Data.teamCulture} />
              <ReviewRow label="Dress Code" value={card4Data.dressCode} />
            </ReviewSection>
          </div>

          {saveError && (
            <p
              role="alert"
              className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
            >
              The opportunity was not saved: {saveError}
            </p>
          )}

          <div
            style={{
              marginTop: 24,
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
            }}
          >
            <button
              type="button"
              onClick={() => setShowReviewModal(false)}
              style={{
                padding: "10px 20px",
                borderRadius: 6,
                border: `1px solid ${C.divider}`,
                background: "#fff",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Edit Form
            </button>
            <button
              type="button"
              onClick={handleDoneConfirm}
              disabled={saving}
              style={{
                padding: "10px 24px",
                borderRadius: 6,
                border: 0,
                background: C.green,
                color: "#fff",
                cursor: "pointer",
                fontWeight: 800,
                fontSize: 14,
              }}
            >
              {saving ? "Publishing..." : "Done & Publish"}
            </button>
          </div>
        </ModalWrapper>
      )}

      {/* DELETE JOB CONFIRMATION MODAL */}
      {jobAction && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999999,
          }}
          onClick={closeJobAction}
        >
          <div
            style={{
              background: "#fff",
              width: "90%",
              maxWidth: 440,
              borderRadius: 16,
              padding: 28,
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
              border: `1px solid ${C.divider}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 22,
                  background: C.redLight,
                  color: C.red,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 20,
                }}
              >
                <InlineIcon name="warning" size={20} />
              </div>
              <div>
                <h3
                  style={{
                    fontSize: 18,
                    fontWeight: 800,
                    color: C.navy,
                    margin: 0,
                  }}
                >
                  {JOB_ACTION_COPY[jobAction.kind].title}
                </h3>
                <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
                  {JOB_ACTION_COPY[jobAction.kind].note}
                </div>
              </div>
            </div>

            <p
              style={{
                fontSize: 14,
                color: C.textDark,
                lineHeight: 1.5,
                margin: "0 0 20px 0",
              }}
            >
              {jobAction.kind === "delete" ? (
                <>
                  Are you sure you want to permanently delete{" "}
                  <strong>"{jobActionTitle}"</strong>?
                </>
              ) : (
                <>
                  Close applications for <strong>"{jobActionTitle}"</strong>? It
                  leaves the Talent Hub and nobody else can apply. You can
                  reopen it with its status switch until its closing date.
                </>
              )}
            </p>

            {jobActionError && (
              <p role="alert" className="mb-5 text-sm font-medium text-red-700">
                {jobActionError}
              </p>
            )}

            <div
              style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}
            >
              <button
                type="button"
                onClick={closeJobAction}
                disabled={jobActionBusy}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: `1px solid ${C.divider}`,
                  background: "#fff",
                  color: C.textDark,
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmJobAction}
                disabled={jobActionBusy}
                style={{
                  padding: "10px 18px",
                  borderRadius: 8,
                  border: 0,
                  background: C.red,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {jobActionBusy
                  ? JOB_ACTION_COPY[jobAction.kind].busy
                  : JOB_ACTION_COPY[jobAction.kind].confirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 1: JOB REQUIREMENTS */}
      {activeModal === 1 && (
        <ModalWrapper
          title="1. Job Requirements Questionnaire"
          onClose={() => setActiveModal(null)}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <label style={labelStyle}>
              <span>
                Opening for{" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <input
                type="text"
                placeholder="e.g. Senior Software Engineer"
                value={card1Data.openingFor}
                onChange={(e) => updateCard1("openingFor", e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor:
                    !card1Data.openingFor.trim() && validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                required
              />
            </label>

            <label style={labelStyle}>
              <span>Age Range</span>
              <select
                value={card1Data.ageRange}
                onChange={(e) => updateCard1("ageRange", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Age Range</option>
                <option value="Any">Any Age</option>
                <option value="18-25">18-25</option>
                <option value="21-30">21-30</option>
                <option value="25-35">25-35</option>
                <option value="30-40">30-40</option>
                <option value="40+">40+</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Gender</span>
              <select
                value={card1Data.gender}
                onChange={(e) => updateCard1("gender", e.target.value)}
                style={inputStyle}
              >
                <option value="Any">Any</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Languages Required</span>
              <input
                type="text"
                placeholder="e.g. English, Tamil, Hindi"
                value={card1Data.languagesRequired}
                onChange={(e) =>
                  updateCard1("languagesRequired", e.target.value)
                }
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              <span>Required Level of Education</span>
              <select
                value={card1Data.educationLevel}
                onChange={(e) => updateCard1("educationLevel", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Level</option>
                <option value="Any">Any</option>
                <option value="High School">High School</option>
                <option value="Higher Secondary">Higher Secondary</option>
                <option value="Diploma">Diploma</option>
                <option value="UG">UG (Undergraduate)</option>
                <option value="PG">PG (Postgraduate)</option>
                <option value="PhD">PhD</option>
                <option value="Post Doctoral">Post Doctoral</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Required Degree</span>
              <input
                type="text"
                placeholder="e.g. B.Tech, B.E., MBA"
                value={card1Data.requiredDegree}
                onChange={(e) => updateCard1("requiredDegree", e.target.value)}
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              <span>Educational Institution</span>
              <input
                type="text"
                placeholder="e.g. Any / Tier-1 / Anna University"
                value={card1Data.educationalInstitution}
                onChange={(e) =>
                  updateCard1("educationalInstitution", e.target.value)
                }
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              <span>Certifications / Licences Required</span>
              <input
                type="text"
                placeholder="e.g. AWS Certified, PMP, CPA"
                value={card1Data.certificationsRequired}
                onChange={(e) =>
                  updateCard1("certificationsRequired", e.target.value)
                }
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              <span>Special Consideration for Military Veterans?</span>
              <select
                value={card1Data.veteranConsideration}
                onChange={(e) =>
                  updateCard1("veteranConsideration", e.target.value)
                }
                style={inputStyle}
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Industry</span>
              <select
                value={card1Data.industryId || card1Data.industry}
                onChange={(e) => {
                  const val = e.target.value;
                  const item = INDUSTRY_MASTER.find(
                    (ind) => ind.id === val || ind.name === val,
                  );
                  setCard1Data((prev) => ({
                    ...prev,
                    industryId: item ? item.id : val,
                    industry: item ? item.name : val,
                  }));
                }}
                style={inputStyle}
              >
                <option value="">Select Industry</option>
                {INDUSTRY_MASTER.map((ind) => (
                  <option key={ind.id} value={ind.id}>
                    {ind.name}
                  </option>
                ))}
              </select>
            </label>

            <label style={labelStyle}>
              <span>Department / Specialisation</span>
              <input
                type="text"
                placeholder="e.g. Engineering / Marketing / Audit"
                value={card1Data.departmentSpecialisation}
                onChange={(e) =>
                  updateCard1("departmentSpecialisation", e.target.value)
                }
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              <span>
                Min Experience Required (Years){" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <select
                value={card1Data.minExpYears}
                onChange={(e) => updateCard1("minExpYears", e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor:
                    !card1Data.minExpYears.trim() && validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                required
              >
                <option value="">Min Exp</option>
                {EXP_MIN_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>

            <label style={labelStyle}>
              <span>
                Max Experience Required (Years){" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <select
                value={card1Data.maxExpYears}
                onChange={(e) => updateCard1("maxExpYears", e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor:
                    !card1Data.maxExpYears.trim() && validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                required
              >
                <option value="">Max Exp</option>
                {EXP_MAX_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </label>

            <label style={labelStyle}>
              <span>Employment Type Offered</span>
              <select
                value={card1Data.employmentType}
                onChange={(e) => updateCard1("employmentType", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Type</option>
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Internship">Internship</option>
                <option value="Freelance">Freelance</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Number of Open Positions</span>
              <input
                type="number"
                min="1"
                placeholder="e.g. 5"
                value={card1Data.openPositions}
                onChange={(e) => updateCard1("openPositions", e.target.value)}
                style={inputStyle}
              />
            </label>

            <label style={{ ...labelStyle, gridColumn: "span 2" }}>
              <span>
                Essential Skillset{" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <textarea
                placeholder="e.g. React.js, Node.js, PostgreSQL, REST APIs"
                value={card1Data.essentialSkillset}
                onChange={(e) =>
                  updateCard1("essentialSkillset", e.target.value)
                }
                style={{
                  ...textareaStyle,
                  borderColor:
                    !card1Data.essentialSkillset.trim() &&
                    validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                rows={3}
                required
              />
            </label>
          </div>
        </ModalWrapper>
      )}

      {/* MODAL 2: COMPENSATION & BENEFITS */}
      {activeModal === 2 && (
        <ModalWrapper
          title="2. Compensation & Benefits"
          onClose={() => setActiveModal(null)}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <label style={labelStyle}>
              <span>
                Minimum Salary{" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <input
                type="text"
                placeholder={MIN_SALARY_EXAMPLE}
                value={card2Data.minSalary}
                onChange={(e) => updateCard2("minSalary", e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor:
                    !card2Data.minSalary.trim() && validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                required
              />
            </label>

            <label style={labelStyle}>
              <span>
                Maximum Salary{" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <input
                type="text"
                placeholder={MAX_SALARY_EXAMPLE}
                value={card2Data.maxSalary}
                onChange={(e) => updateCard2("maxSalary", e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor:
                    !card2Data.maxSalary.trim() && validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                required
              />
            </label>

            <label style={labelStyle}>
              <span>Joining Bonus Offered?</span>
              <select
                value={card2Data.joiningBonus}
                onChange={(e) => updateCard2("joiningBonus", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Performance Bonus / Variable Pay Component?</span>
              <select
                value={card2Data.performanceBonus}
                onChange={(e) =>
                  updateCard2("performanceBonus", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Health Insurance Provided (Self + Family)?</span>
              <select
                value={card2Data.healthInsurance}
                onChange={(e) => updateCard2("healthInsurance", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>
                Provident Fund / ESI / Statutory Benefits Provided?{" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <select
                value={card2Data.statutoryBenefits}
                onChange={(e) =>
                  updateCard2("statutoryBenefits", e.target.value)
                }
                style={{
                  ...inputStyle,
                  borderColor:
                    !card2Data.statutoryBenefits.trim() &&
                    validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                required
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>ESOPs / Employee Stock Options Offered?</span>
              <select
                value={card2Data.esopsOffered}
                onChange={(e) => updateCard2("esopsOffered", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>
                Learning & Development Budget / Sponsored Certifications?
              </span>
              <select
                value={card2Data.learningBudget}
                onChange={(e) => updateCard2("learningBudget", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>
                Notice Period{" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <select
                value={card2Data.noticePeriod}
                onChange={(e) => updateCard2("noticePeriod", e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor:
                    !card2Data.noticePeriod.trim() &&
                    validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                required
              >
                <option value="">Select Notice Period</option>
                <option value="Immediate">Immediate</option>
                <option value="15 days">15 days</option>
                <option value="1 month">1 month</option>
                <option value="2 months">2 months</option>
                <option value="3 months">3 months</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Minimum Months Candidate Expected to Stay</span>
              <select
                value={card2Data.minimumStayMonths}
                onChange={(e) =>
                  updateCard2("minimumStayMonths", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select Duration</option>
                <option value="Work at will">Work at will</option>
                {MIN_STAY_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt} months
                  </option>
                ))}
              </select>
            </label>
          </div>
        </ModalWrapper>
      )}

      {/* MODAL 3: WORK CONDITIONS */}
      {activeModal === 3 && (
        <ModalWrapper
          title="3. Work Conditions"
          onClose={() => setActiveModal(null)}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <label style={labelStyle}>
              <span>Working Model</span>
              <select
                value={card3Data.workingModel}
                onChange={(e) => updateCard3("workingModel", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Working Model</option>
                <option value="Office">Office</option>
                <option value="Work from home">Work from home</option>
                <option value="Hybrid">Hybrid</option>
                <option value="Any">Any</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>
                Job Location{" "}
                <span style={{ color: C.red, fontWeight: 800 }}>
                  * (Mandatory)
                </span>
              </span>
              <select
                value={card3Data.jobLocation}
                onChange={(e) => updateCard3("jobLocation", e.target.value)}
                style={{
                  ...inputStyle,
                  borderColor:
                    !card3Data.jobLocation.trim() && validationErrors.length > 0
                      ? C.red
                      : C.divider,
                }}
                required
              >
                <option value="">Select Location</option>
                {DISTRICTS.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>

            <label style={labelStyle}>
              <span>Does the job involve local travel?</span>
              <select
                value={card3Data.localTravel}
                onChange={(e) => updateCard3("localTravel", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Does the job involve domestic travel?</span>
              <select
                value={card3Data.domesticTravel}
                onChange={(e) => updateCard3("domesticTravel", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Does the job involve international travel?</span>
              <select
                value={card3Data.internationalTravel}
                onChange={(e) =>
                  updateCard3("internationalTravel", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Work Shift</span>
              <select
                value={card3Data.workShift}
                onChange={(e) => updateCard3("workShift", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Shift</option>
                <option value="Day">Day</option>
                <option value="Evening">Evening</option>
                <option value="Night">Night</option>
                <option value="Any">Any</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Expected Hours of Work Per Day</span>
              <select
                value={card3Data.expectedHoursPerDay}
                onChange={(e) =>
                  updateCard3("expectedHoursPerDay", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select Hours</option>
                <option value="less than 4 hours">less than 4 hours</option>
                <option value="4-8 hours">4-8 hours</option>
                <option value="8-9">8-9 hours</option>
                <option value="9-10">9-10 hours</option>
                <option value="more than 10">more than 10 hours</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Requirement to be flexible with work timings?</span>
              <select
                value={card3Data.flexibleHours}
                onChange={(e) => updateCard3("flexibleHours", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Expected to work beyond shift timings?</span>
              <select
                value={card3Data.extendBeyondHours}
                onChange={(e) =>
                  updateCard3("extendBeyondHours", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Is weekend work required?</span>
              <select
                value={card3Data.weekendWork}
                onChange={(e) => updateCard3("weekendWork", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Team Size & Reporting to (Designation)</span>
              <input
                type="text"
                placeholder="e.g. Team of 6, Reports to VP Engineering"
                value={card3Data.reportingStructure}
                onChange={(e) =>
                  updateCard3("reportingStructure", e.target.value)
                }
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              <span>Probation Period (in months)</span>
              <select
                value={card3Data.probationMonths}
                onChange={(e) => updateCard3("probationMonths", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Probation</option>
                <option value="No probation">No probation</option>
                <option value="0">0 months</option>
                <option value="1">1 month</option>
                <option value="2">2 months</option>
                <option value="3">3 months</option>
                <option value="4">4 months</option>
                <option value="5">5 months</option>
                <option value="6">6 months</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Interview Mode</span>
              <select
                value={card3Data.interviewMode}
                onChange={(e) => updateCard3("interviewMode", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Mode</option>
                <option value="Virtual">Virtual</option>
                <option value="In-person">In-person</option>
                <option value="Hybrid">Hybrid</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Interview Rounds</span>
              <select
                value={card3Data.interviewRounds}
                onChange={(e) => updateCard3("interviewRounds", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Rounds</option>
                <option value="1 round">1 round</option>
                <option value="2 rounds">2 rounds</option>
                <option value="3 rounds">3 rounds</option>
                <option value="4 rounds">4 rounds</option>
                <option value="5+ rounds">5+ rounds</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Background Verification Required?</span>
              <select
                value={card3Data.bgvRequired}
                onChange={(e) => updateCard3("bgvRequired", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Visa / Relocation Sponsorship Offered?</span>
              <select
                value={card3Data.visaSponsorship}
                onChange={(e) => updateCard3("visaSponsorship", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Laptop / Equipment Provided by Company?</span>
              <select
                value={card3Data.companyEquipment}
                onChange={(e) =>
                  updateCard3("companyEquipment", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>
          </div>
        </ModalWrapper>
      )}

      {/* MODAL 4: CULTURE & PERKS */}
      {activeModal === 4 && (
        <ModalWrapper
          title="4. Culture & Perks"
          onClose={() => setActiveModal(null)}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <label style={labelStyle}>
              <span>Pick up and drop services offered?</span>
              <select
                value={card4Data.cabService}
                onChange={(e) => updateCard4("cabService", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Food / Cafeteria provided?</span>
              <select
                value={card4Data.foodProvided}
                onChange={(e) => updateCard4("foodProvided", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Leave Policy (CL, SL and EL provided)?</span>
              <select
                value={card4Data.leavePolicyCLELEL}
                onChange={(e) =>
                  updateCard4("leavePolicyCLELEL", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Safety measures for women (late-night cab / escort)?</span>
              <select
                value={card4Data.safetyProvisions}
                onChange={(e) =>
                  updateCard4("safetyProvisions", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Mental health / wellness support offered?</span>
              <select
                value={card4Data.wellnessSupport}
                onChange={(e) => updateCard4("wellnessSupport", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Active D&I policies in place?</span>
              <select
                value={card4Data.diversityInclusion}
                onChange={(e) =>
                  updateCard4("diversityInclusion", e.target.value)
                }
                style={inputStyle}
              >
                <option value="">Select</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </label>

            <label style={labelStyle}>
              <span>Dress Code</span>
              <select
                value={card4Data.dressCode}
                onChange={(e) => updateCard4("dressCode", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Dress Code</option>
                <option value="Formal">Formal</option>
                <option value="Business casual">Business casual</option>
                <option value="Casual">Casual</option>
                <option value="Any">Any</option>
              </select>
            </label>

            <label style={{ ...labelStyle, gridColumn: "span 2" }}>
              <span>
                Reporting Manager's Working Style / Team Culture (One line)
              </span>
              <input
                type="text"
                placeholder="e.g. Collaborative, flexible, ownership-driven"
                value={card4Data.teamCulture}
                onChange={(e) => updateCard4("teamCulture", e.target.value)}
                style={inputStyle}
              />
            </label>
          </div>
        </ModalWrapper>
      )}

      {/* PUBLISHED JOB DETAILS MODAL (ONLY SHOWS MANDATORY & FILLED DATA) */}
      {selectedJobDetails && (
        <ModalWrapper
          title={`Published Opportunity Details: ${selectedJobDetails.title}`}
          onClose={() => setSelectedJobDetails(null)}
        >
          <div
            style={{
              background: C.surface,
              padding: 14,
              borderRadius: 8,
              marginBottom: 16,
              border: `1px solid ${C.divider}`,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: C.navy }}>
              Published Opportunity Details
            </div>
            <div style={{ fontSize: 11, color: C.textMed, marginTop: 2 }}>
              Only mandatory fields and configured optional fields are displayed
              below. Unfilled optional fields are hidden.
            </div>
          </div>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              fontSize: 13,
            }}
          >
            {/* General Opportunity Information */}
            <ReviewSection title="General Opportunity Information">
              <DetailRow
                label="Opportunity Title"
                value={selectedJobDetails.title}
                isMandatory
              />
              <DetailRow
                label="Job Description"
                value={selectedJobDetails.jobDescription}
                isMandatory
              />
              <DetailRow
                label="Application Start Date (From)"
                value={selectedJobDetails.fromDate}
                isMandatory
              />
              <DetailRow
                label="Application Closing Date (To)"
                value={selectedJobDetails.toDate}
                isMandatory
              />
              <DetailRow
                label="Post Status"
                value={jobStatusLabel(selectedJobDetails)}
                isMandatory
              />
              <DetailRow
                label="Posted On"
                value={selectedJobDetails.postedAt}
                isMandatory
              />
            </ReviewSection>

            {/* 1. Job Requirements */}
            <ReviewSection title="1. Job Requirements">
              <DetailRow
                label="Opening For"
                value={selectedJobDetails.card1?.openingFor}
                isMandatory
              />
              <DetailRow
                label="Required Degree"
                value={selectedJobDetails.card1?.requiredDegree}
              />
              <DetailRow
                label="Min Experience (Years)"
                value={
                  selectedJobDetails.card1?.minExpYears
                    ? `${selectedJobDetails.card1.minExpYears} years`
                    : ""
                }
                isMandatory
              />
              <DetailRow
                label="Max Experience (Years)"
                value={
                  selectedJobDetails.card1?.maxExpYears
                    ? `${selectedJobDetails.card1.maxExpYears} years`
                    : ""
                }
                isMandatory
              />
              <DetailRow
                label="Essential Skillset"
                value={selectedJobDetails.card1?.essentialSkillset}
                isMandatory
              />

              <DetailRow
                label="Education Level"
                value={selectedJobDetails.card1?.educationLevel}
              />
              <DetailRow
                label="Educational Institution"
                value={selectedJobDetails.card1?.educationalInstitution}
              />
              <DetailRow
                label="Age Range"
                value={selectedJobDetails.card1?.ageRange}
              />
              <DetailRow
                label="Gender Preference"
                value={selectedJobDetails.card1?.gender}
              />
              <DetailRow
                label="Languages Required"
                value={selectedJobDetails.card1?.languagesRequired}
              />
              <DetailRow
                label="Certifications Required"
                value={selectedJobDetails.card1?.certificationsRequired}
              />
              <DetailRow
                label="Veteran Consideration"
                value={selectedJobDetails.card1?.veteranConsideration}
              />
              <DetailRow
                label="Industry"
                value={selectedJobDetails.card1?.industry}
              />
              <DetailRow
                label="Department / Specialisation"
                value={selectedJobDetails.card1?.departmentSpecialisation}
              />
              <DetailRow
                label="Employment Type"
                value={selectedJobDetails.card1?.employmentType}
              />
              <DetailRow
                label="Open Positions"
                value={selectedJobDetails.card1?.openPositions}
              />
            </ReviewSection>

            {/* 2. Compensation & Benefits */}
            <ReviewSection title="2. Compensation & Benefits">
              <DetailRow
                label="Minimum Salary"
                value={selectedJobDetails.card2?.minSalary}
                isMandatory
              />
              <DetailRow
                label="Maximum Salary"
                value={selectedJobDetails.card2?.maxSalary}
                isMandatory
              />
              <DetailRow
                label="Statutory Benefits (PF / ESI)"
                value={selectedJobDetails.card2?.statutoryBenefits}
                isMandatory
              />
              <DetailRow
                label="Notice Period"
                value={selectedJobDetails.card2?.noticePeriod}
                isMandatory
              />

              <DetailRow
                label="Joining Bonus"
                value={selectedJobDetails.card2?.joiningBonus}
              />
              <DetailRow
                label="Performance Bonus"
                value={selectedJobDetails.card2?.performanceBonus}
              />
              <DetailRow
                label="Health Insurance"
                value={selectedJobDetails.card2?.healthInsurance}
              />
              <DetailRow
                label="ESOPs Offered"
                value={selectedJobDetails.card2?.esopsOffered}
              />
              <DetailRow
                label="Learning Budget"
                value={selectedJobDetails.card2?.learningBudget}
              />
              <DetailRow
                label="Minimum Stay Commitment"
                value={
                  selectedJobDetails.card2?.minimumStayMonths
                    ? `${selectedJobDetails.card2.minimumStayMonths} months`
                    : ""
                }
              />
            </ReviewSection>

            {/* 3. Work Conditions */}
            <ReviewSection title="3. Work Conditions">
              <DetailRow
                label="Job Location"
                value={selectedJobDetails.card3?.jobLocation}
                isMandatory
              />

              <DetailRow
                label="Working Model"
                value={selectedJobDetails.card3?.workingModel}
              />
              <DetailRow
                label="Local Travel Required"
                value={selectedJobDetails.card3?.localTravel}
              />
              <DetailRow
                label="Domestic Travel Required"
                value={selectedJobDetails.card3?.domesticTravel}
              />
              <DetailRow
                label="International Travel Required"
                value={selectedJobDetails.card3?.internationalTravel}
              />
              <DetailRow
                label="Work Shift"
                value={selectedJobDetails.card3?.workShift}
              />
              <DetailRow
                label="Max Work Hours / Day"
                value={selectedJobDetails.card3?.maxHoursPerDay}
              />
              <DetailRow
                label="Flexible Work Hours"
                value={selectedJobDetails.card3?.flexibleHours}
              />
              <DetailRow
                label="Extend Beyond Hours"
                value={selectedJobDetails.card3?.extendBeyondHours}
              />
              <DetailRow
                label="Weekend Work Required"
                value={selectedJobDetails.card3?.weekendWork}
              />
              <DetailRow
                label="Reporting Structure"
                value={selectedJobDetails.card3?.reportingStructure}
              />
              <DetailRow
                label="Probation Months"
                value={selectedJobDetails.card3?.probationMonths}
              />
              <DetailRow
                label="Interview Mode"
                value={selectedJobDetails.card3?.interviewMode}
              />
              <DetailRow
                label="Background Check"
                value={selectedJobDetails.card3?.backgroundCheck}
              />
              <DetailRow
                label="Visa Sponsorship"
                value={selectedJobDetails.card3?.visaSponsorship}
              />
              <DetailRow
                label="Company Equipment Provided"
                value={selectedJobDetails.card3?.companyEquipment}
              />
            </ReviewSection>

            {/* 4. Culture & Perks */}
            <ReviewSection title="4. Culture & Perks">
              <DetailRow
                label="Cab Service"
                value={selectedJobDetails.card4?.cabService}
              />
              <DetailRow
                label="Cafeteria Facility"
                value={selectedJobDetails.card4?.cafeteria}
              />
              <DetailRow
                label="Leave Policy"
                value={selectedJobDetails.card4?.leavePolicy}
              />
              <DetailRow
                label="Safety Provisions"
                value={selectedJobDetails.card4?.safetyProvisions}
              />
              <DetailRow
                label="Wellness Support"
                value={selectedJobDetails.card4?.wellnessSupport}
              />
              <DetailRow
                label="Diversity & Inclusion"
                value={selectedJobDetails.card4?.diversityInclusion}
              />
              <DetailRow
                label="Team Culture"
                value={selectedJobDetails.card4?.teamCulture}
              />
              <DetailRow
                label="Dress Code"
                value={selectedJobDetails.card4?.dressCode}
              />
            </ReviewSection>
          </div>
        </ModalWrapper>
      )}
    </AppShell>
  );
}

// Review UI Helper Components
function ReviewSection({ title, children }) {
  const childArray = Children.toArray(children);
  const hasVisibleChild = childArray.some((child) => {
    if (!child || !child.props) return false;
    const { value, isMandatory } = child.props;
    if (isMandatory) return true;
    const stringVal =
      value !== null && value !== undefined ? String(value).trim() : "";
    return (
      stringVal !== "" &&
      stringVal !== "Select" &&
      stringVal !== "Select Type" &&
      stringVal !== "Min Exp" &&
      stringVal !== "Max Exp" &&
      stringVal !== "-" &&
      !stringVal.includes("undefined") &&
      !stringVal.startsWith(" (") &&
      stringVal !== "Shift (hrs/day)"
    );
  });

  if (!hasVisibleChild) return null;

  return (
    <div
      style={{
        border: `1px solid ${C.divider}`,
        borderRadius: 8,
        padding: 14,
        background: "#fafafa",
      }}
    >
      <h4
        style={{
          fontSize: 13,
          fontWeight: 800,
          color: C.navy,
          margin: "0 0 10px",
          textTransform: "uppercase",
        }}
      >
        {title}
      </h4>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {children}
      </div>
    </div>
  );
}

function ReviewRow({ label, value, isMandatory = false }) {
  const stringVal =
    value !== null && value !== undefined ? String(value).trim() : "";
  const hasValue =
    stringVal !== "" &&
    stringVal !== "Select" &&
    stringVal !== "Select Type" &&
    stringVal !== "Min Exp" &&
    stringVal !== "Max Exp" &&
    stringVal !== "-" &&
    !stringVal.includes("undefined") &&
    !stringVal.startsWith(" (") &&
    stringVal !== "Shift (hrs/day)";

  if (!isMandatory && !hasValue) {
    return null; // Hide unfilled optional fields completely!
  }

  return (
    <div
      style={{
        background: "#fff",
        padding: "8px 12px",
        borderRadius: 6,
        border: `1px solid ${C.divider}`,
      }}
    >
      <div style={{ fontSize: 11, color: C.textLight }}>
        <span>{label}</span>
      </div>
      <div
        style={{
          fontWeight: 600,
          color: C.textDark,
          fontSize: 13,
          marginTop: 2,
          wordBreak: "break-word",
        }}
      >
        {hasValue ? value : <span style={{ color: C.red }}>Not Specified</span>}
      </div>
    </div>
  );
}

function DetailRow({ label, value, isMandatory = false }) {
  const stringVal =
    value !== null && value !== undefined ? String(value).trim() : "";
  const hasValue =
    stringVal !== "" &&
    stringVal !== "Select" &&
    stringVal !== "Select Type" &&
    stringVal !== "Min Exp" &&
    stringVal !== "Max Exp" &&
    stringVal !== "-" &&
    !stringVal.includes("undefined") &&
    !stringVal.startsWith(" (") &&
    stringVal !== "Shift (hrs/day)";

  if (!isMandatory && !hasValue) {
    return null; // Hide unfilled optional fields completely!
  }

  return (
    <div
      style={{
        background: "#fff",
        padding: "8px 12px",
        borderRadius: 6,
        border: `1px solid ${C.divider}`,
      }}
    >
      <div style={{ fontSize: 11, color: C.textLight }}>
        <span>{label}</span>
      </div>
      <div
        style={{
          fontWeight: 600,
          color: C.textDark,
          fontSize: 13,
          marginTop: 2,
          wordBreak: "break-word",
        }}
      >
        {hasValue ? value : <span style={{ color: C.red }}>Not Specified</span>}
      </div>
    </div>
  );
}

// Shared Modal Wrapper
function ModalWrapper({ title, children, onClose }) {
  return (
    <div style={modalOverlayStyle}>
      <div style={modalContentStyle}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${C.divider}`,
            paddingBottom: 12,
            marginBottom: 20,
          }}
        >
          <h2
            style={{ fontSize: 18, fontWeight: 800, color: C.navy, margin: 0 }}
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "transparent",
              border: 0,
              fontSize: 22,
              cursor: "pointer",
              color: C.textMed,
            }}
          >
            <InlineIcon name="close" size={20} />
          </button>
        </div>
        {children}
        <div
          style={{
            marginTop: 24,
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "10px 24px",
              borderRadius: 6,
              border: 0,
              background: C.primary,
              color: "#fff",
              cursor: "pointer",
              fontWeight: 700,
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// Complete Data Arrays
const EXP_MIN_OPTIONS = [
  "Fresher",
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "20",
  "25",
  "30",
  "35",
  "over 35 years",
];
const EXP_MAX_OPTIONS = [
  "0",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "13",
  "14",
  "15",
  "20",
  "25",
  "30",
  "35",
  "40",
  "over 40 years",
];
const MIN_STAY_OPTIONS = [
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "18",
  "24",
  "36",
  "48",
  "60",
];

const INDUSTRIES = [
  "Accounting / Auditing",
  "Advertising & Marketing",
  "Agriculture / Forestry / Fishing",
  "Analytics / KPO / Research",
  "Animation & VFX",
  "Architecture / Interior Design",
  "Auto components",
  "Automobile",
  "Aviation",
  "Banking",
  "Beverages",
  "Biotechnology",
  "BPO / Call centre",
  "Building materials",
  "Chemicals",
  "Clinical Research / Contract Research",
  "Consumer Electronics & Appliances",
  "Courier & Logistics",
  "Defence and Aerospace",
  "Design",
  "Education / Training",
  "E-Learning / Ed Tech",
  "Electrical Equipment",
  "Electronic Component / Semiconductors",
  "Electronics Manufacturing",
  "Emerging Technologies",
  "Engineering and construction",
  "Events & Live Entertainment",
  "Facility Management Services",
  "Fertilisers / Pesticides / Agrochemicals",
  "Film / Music / Entertainment",
  "Fin Tech / Payments",
  "Financial Services",
  "Fitness & Wellness",
  "FMCG",
  "Food Processing",
  "Gaming",
  "Gems & Jewellery",
  "Government / Public Administration",
  "Hardware and Networking",
  "Hotels & Restaurants",
  "Import & Export",
  "Industrial Automation",
  "Industrial Equipment / Machinery",
  "Insurance",
  "Internet",
  "Investment Banking / Venture Capital / Private Equity",
  "Iron and Steel",
  "IT Services and consulting",
  "Law Enforcement / Security",
  "Leather",
  "Legal",
  "Management consulting",
  "Medical Devices and Equipments",
  "Medical Services / Hospitals",
  "Metals and Mining",
  "NBFC",
  "NGO / Social Services / Industry Associations",
  "Oil and Gas",
  "Packaging and Containers",
  "Petrochemicals / Plastics and Rubber",
  "Pharmaceuticals / Life Sciences",
  "Ports and Shipping",
  "Power",
  "Printing and Publishing",
  "Railways",
  "Real Estate",
  "Recruitment / staffing",
  "Retail",
  "Software Product",
  "Telecom / ISP",
  "Textile & Apparel",
  "Travel & Tourism",
  "TV and Radio",
  "Water Treatment / Waste management",
  "Others",
];

const DISTRICTS = [
  "Ariyalur",
  "Chengalpattu",
  "Chennai",
  "Coimbatore",
  "Cuddalore",
  "Dharmapuri",
  "Dindigul",
  "Erode",
  "Kallakurichi",
  "Kanchipuram",
  "Kanyakumari",
  "Karur",
  "Krishnagiri",
  "Madurai",
  "Mayiladuthurai",
  "Nagapattinam",
  "Namakkal",
  "Nilgiris",
  "Perambalur",
  "Pudukkottai",
  "Ramanathapuram",
  "Ranipet",
  "Salem",
  "Sivaganga",
  "Tenkasi",
  "Thanjavur",
  "Theni",
  "Thoothukudi",
  "Tiruchirappalli",
  "Tirunelveli",
  "Tirupathur",
  "Tiruppur",
  "Tiruvallur",
  "Tiruvannamalai",
  "Tiruvarur",
  "Vellore",
  "Viluppuram",
  "Virudhunagar",
  "Bengaluru",
  "Hyderabad",
  "Mumbai",
  "Delhi NCR",
  "Others",
];

// Reusable Styles
const interactiveCardStyle = {
  background: "#f8fafc",
  border: `2px solid ${C.primary}`,
  borderRadius: 8,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
};

const cardHeaderStyle = {
  fontSize: 12,
  fontWeight: 800,
  color: C.primaryDark,
  background: C.primaryLight,
  padding: "10px 12px",
  margin: 0,
  textTransform: "uppercase",
  borderBottom: `1px solid ${C.divider}`,
};

const cardBodyStyle = {
  padding: 12,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  flex: 1,
};
const cardSummaryTextStyle = {
  fontSize: 12,
  color: C.textMed,
  lineHeight: 1.5,
};
const cardLinkTextStyle = {
  marginTop: "auto",
  fontSize: 11,
  fontWeight: 700,
  color: C.primary,
};
const labelStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
  fontSize: 12,
  fontWeight: 700,
  color: C.textDark,
};
const inputStyle = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 6,
  border: `1px solid ${C.divider}`,
  fontSize: 13,
  boxSizing: "border-box",
  fontFamily: "inherit",
};
const textareaStyle = { ...inputStyle, resize: "vertical" };
const submitButtonStyle = {
  padding: "12px 28px",
  background: C.primary,
  color: "#fff",
  fontSize: 15,
  fontWeight: 700,
  borderRadius: 8,
  border: 0,
  cursor: "pointer",
  textAlign: "center",
  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
  transition: "background 0.2s ease, transform 0.1s ease",
};
const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.6)",
  backdropFilter: "blur(3px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
  padding: 20,
};
const modalContentStyle = {
  background: "#fff",
  borderRadius: 12,
  width: "100%",
  maxWidth: 820,
  maxHeight: "90vh",
  overflowY: "auto",
  padding: 28,
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1)",
};

// What the confirmation says for each way of taking a job down: deleting
// while nobody has applied, closing after (see utils/hiring.js).
const JOB_ACTION_COPY = {
  delete: {
    title: "Delete Opportunity?",
    note: "This action cannot be undone",
    confirm: "Yes, Delete Job",
    busy: "Deleting...",
  },
  close: {
    title: "Close Opportunity?",
    note: "People have applied, so their applications are kept",
    confirm: "Yes, Close Job",
    busy: "Closing...",
  },
};

// Example yearly salaries in the salary placeholders (12,00,000 and
// 18,00,000 rupees, in paise), in the shared rupee format.
const MIN_SALARY_EXAMPLE = `e.g. ${formatRupees(1200000 * 100)}`;
const MAX_SALARY_EXAMPLE = `e.g. ${formatRupees(1800000 * 100)}`;
